// Visual Acuity Assessment Engine
// Implements LogMAR-standard visual acuity testing with screen calibration
// Reference: ETDRS chart design (Ferris et al. 1982), LogMAR scoring

// 10 Sloan letters — the only optotypes validated for LogMAR/ETDRS charts.
// Each letter is designed to be equally legible at threshold.
const SLOAN_LETTERS = ['C', 'D', 'H', 'K', 'N', 'O', 'R', 'S', 'V', 'Z'];

// Standard LogMAR chart: 14 lines from 1.0 (20/200) down to -0.3 (20/10)
// Each line = 0.1 LogMAR step.  Size ratio between lines = 10^0.1 ≈ 1.2589
const LOGMAR_LINES = [
    { logMAR: 1.0, snellenFt: '20/200', snellenM: '6/60',  letters: 1 },
    { logMAR: 0.9, snellenFt: '20/160', snellenM: '6/48',  letters: 2 },
    { logMAR: 0.8, snellenFt: '20/125', snellenM: '6/38',  letters: 3 },
    { logMAR: 0.7, snellenFt: '20/100', snellenM: '6/30',  letters: 4 },
    { logMAR: 0.6, snellenFt: '20/80',  snellenM: '6/24',  letters: 5 },
    { logMAR: 0.5, snellenFt: '20/63',  snellenM: '6/19',  letters: 5 },
    { logMAR: 0.4, snellenFt: '20/50',  snellenM: '6/15',  letters: 5 },
    { logMAR: 0.3, snellenFt: '20/40',  snellenM: '6/12',  letters: 5 },
    { logMAR: 0.2, snellenFt: '20/32',  snellenM: '6/9.5', letters: 5 },
    { logMAR: 0.1, snellenFt: '20/25',  snellenM: '6/7.5', letters: 5 },
    { logMAR: 0.0, snellenFt: '20/20',  snellenM: '6/6',   letters: 5 },
    { logMAR:-0.1, snellenFt: '20/16',  snellenM: '6/4.8', letters: 5 },
    { logMAR:-0.2, snellenFt: '20/12.5',snellenM: '6/3.8', letters: 5 },
    { logMAR:-0.3, snellenFt: '20/10',  snellenM: '6/3',   letters: 5 },
];

// Standard credit card dimensions (ISO/IEC 7810 ID-1)
const CREDIT_CARD_WIDTH_MM = 85.6;
const CREDIT_CARD_HEIGHT_MM = 53.98;

// Default test distance in mm (2 meters for distance VA)
const DEFAULT_TEST_DISTANCE_MM = 2000;

/**
 * Calculate pixels per millimeter from a calibration reference.
 * User drags a rectangle to match a credit card's width on screen.
 * @param {number} cardWidthPx - measured width in CSS pixels
 * @returns {number} px/mm ratio
 */
export function calibratePxPerMm(cardWidthPx) {
    return cardWidthPx / CREDIT_CARD_WIDTH_MM;
}

/**
 * Calculate the required optotype height in CSS pixels for a given LogMAR line.
 *
 * A LogMAR value of 0.0 means the minimum angle of resolution (MAR) is 1 arcminute.
 * The full letter subtends 5× MAR (5 arcminutes at 20/20).
 *
 * Formula:
 *   MAR (arcmin) = 10^logMAR
 *   letter angle (rad) = 5 × MAR × (π / 10800)
 *   letter height (mm) = 2 × distance_mm × tan(letter_angle / 2)
 *   letter height (px) = letter_height_mm × pxPerMm
 *
 * @param {number} logMAR - the LogMAR value of the line
 * @param {number} distanceMm - viewing distance in millimeters
 * @param {number} pxPerMm - calibrated px/mm ratio
 * @returns {number} optotype height in CSS pixels
 */
export function optotypeHeightPx(logMAR, distanceMm, pxPerMm) {
    const mar = Math.pow(10, logMAR); // MAR in arcminutes
    const letterAngleRad = 5 * mar * (Math.PI / 10800);
    const heightMm = 2 * distanceMm * Math.tan(letterAngleRad / 2);
    return heightMm * pxPerMm;
}

/**
 * Generate a randomised set of Sloan letters for one line.
 * Letters are drawn without replacement (no repeats within a line).
 * @param {number} count - how many letters (1-5)
 * @returns {string[]} array of single-character strings
 */
export function generateLineLetters(count) {
    const pool = [...SLOAN_LETTERS];
    const result = [];
    for (let i = 0; i < Math.min(count, pool.length); i++) {
        const idx = Math.floor(Math.random() * pool.length);
        result.push(pool.splice(idx, 1)[0]);
    }
    return result;
}

/**
 * Generate the full chart data: an array of line objects, each with
 * randomised letters and the correct font size for the calibration.
 * @param {number} pxPerMm
 * @param {number} distanceMm
 * @returns {Array<{logMAR: number, snellenFt: string, snellenM: string, letters: string[], heightPx: number}>}
 */
export function generateChart(pxPerMm, distanceMm = DEFAULT_TEST_DISTANCE_MM) {
    return LOGMAR_LINES.map(line => ({
        logMAR: line.logMAR,
        snellenFt: line.snellenFt,
        snellenM: line.snellenM,
        letters: generateLineLetters(line.letters),
        heightPx: optotypeHeightPx(line.logMAR, distanceMm, pxPerMm),
    }));
}

/**
 * Score the assessment using per-letter LogMAR scoring.
 *
 * Standard ETDRS scoring:
 *   LogMAR VA = LogMAR of the best line where ≥3 letters are correct
 *             − 0.02 × (extra letters read correctly on the next line)
 *
 * Simplified for self-assessment: user indicates the last line they can
 * read clearly + how many letters on the next line they can still see.
 *
 * @param {number} lastFullLineIndex - 0-based index of the smallest line fully readable
 * @param {number} extraLetters - additional letters read on the next line (0-5)
 * @returns {{logMAR: number, snellenFt: string, snellenM: string, decimal: number, description: string}}
 */
export function scoreAssessment(lastFullLineIndex, extraLetters = 0) {
    const clampedIdx = Math.max(0, Math.min(lastFullLineIndex, LOGMAR_LINES.length - 1));
    const baseLine = LOGMAR_LINES[clampedIdx];
    const logMAR = +(baseLine.logMAR - extraLetters * 0.02).toFixed(2);

    // Decimal VA = 1 / 10^logMAR  (i.e. 20/20 → 1.0)
    const decimal = +(Math.pow(10, -logMAR)).toFixed(2);

    // Find closest Snellen for display
    let snellenFt = baseLine.snellenFt;
    let snellenM = baseLine.snellenM;
    if (extraLetters > 0 && clampedIdx + 1 < LOGMAR_LINES.length) {
        // Interpolated — show the base Snellen with a "+" annotation
        snellenFt = baseLine.snellenFt + '+' + extraLetters;
    }

    return {
        logMAR,
        snellenFt,
        snellenM,
        decimal,
        description: getAcuityDescription(logMAR),
    };
}

/**
 * Human-readable description of the acuity level.
 * @param {number} logMAR
 * @returns {string}
 */
function getAcuityDescription(logMAR) {
    if (logMAR <= -0.1) return 'excellent';      // better than 20/20
    if (logMAR <= 0.0)  return 'normal';          // 20/20
    if (logMAR <= 0.2)  return 'near_normal';     // 20/25 – 20/32
    if (logMAR <= 0.5)  return 'mild_impairment'; // 20/40 – 20/63
    if (logMAR <= 0.7)  return 'moderate';        // 20/80 – 20/100
    return 'significant';                         // worse than 20/100
}

/**
 * The list of test distances the user can choose from (in mm).
 */
export const TEST_DISTANCES = [
    { mm: 2000, label: '2 m / 6.5 ft', key: 'far' },
    { mm: 1000, label: '1 m / 3 ft', key: 'medium' },
    { mm: 400,  label: '40 cm / 16 in', key: 'near' },
];

/**
 * Detect whether the user is likely on a mobile/tablet device.
 * Uses screen width + touch capability as heuristic.
 */
export function isMobileDevice() {
    if (typeof window === 'undefined') return false;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const smallScreen = window.innerWidth <= 768;
    return hasTouch && smallScreen;
}

/**
 * Estimate px/mm from devicePixelRatio and a standard assumption.
 * Most screens are roughly 96 CSS-dpi (3.78 px/mm).  HiDPI mobile
 * screens report higher devicePixelRatio but CSS pixels stay the same,
 * so 96 dpi / 25.4 mm ≈ 3.78 is a reasonable fallback.
 * This is NOT as accurate as credit-card calibration but lets users
 * start a quick test immediately.
 */
export function estimatePxPerMm() {
    return 96 / 25.4; // ~3.78 px/mm at standard CSS resolution
}

/**
 * Get sensible defaults for a quick test (no calibration needed).
 * Mobile → 40cm near-vision test.  Desktop → 1m test.
 */
export function getQuickTestDefaults() {
    const mobile = isMobileDevice();
    return {
        pxPerMm: estimatePxPerMm(),
        distanceMm: mobile ? 400 : 1000,
        distanceLabel: mobile ? '40 cm / 16 in' : '1 m / 3 ft',
        isMobile: mobile,
    };
}

/**
 * Compute trend direction from a list of vision test results for one eye.
 * Compares the average of the last 3 tests to the average of the 3 before that.
 * @param {Array<{logMAR: number}>} tests - ordered oldest→newest
 * @returns {'improving'|'stable'|'declining'|'insufficient'}
 */
export function computeTrend(tests) {
    if (tests.length < 2) return 'insufficient';

    const recent = tests.slice(-3);
    const recentAvg = recent.reduce((s, t) => s + t.logMAR, 0) / recent.length;

    if (tests.length < 4) {
        // Only have 2-3 tests; compare first vs last
        const first = tests[0].logMAR;
        const diff = recentAvg - first;
        if (diff < -0.1) return 'improving';   // logMAR went down = better
        if (diff > 0.1) return 'declining';     // logMAR went up = worse
        return 'stable';
    }

    const older = tests.slice(-6, -3);
    if (older.length === 0) return 'stable';
    const olderAvg = older.reduce((s, t) => s + t.logMAR, 0) / older.length;

    const diff = recentAvg - olderAvg;
    if (diff < -0.1) return 'improving';
    if (diff > 0.1) return 'declining';
    return 'stable';
}

export { LOGMAR_LINES, SLOAN_LETTERS, CREDIT_CARD_WIDTH_MM, CREDIT_CARD_HEIGHT_MM, DEFAULT_TEST_DISTANCE_MM };
